import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL_WITH_API } from '../config/api';
import { getEffectiveToken } from '../utils/authSession';
import { toast } from '@/hooks/use-toast';

interface WorkspaceSource {
  type: 'ou' | 'group';
  id: string;
  name: string;
  email?: string | null;
  directMembersCount?: number | null;
  studentSourceBlocked?: boolean;
  studentSourceWarning?: string | null;
}

interface WorkspaceConnection {
  status?: string;
  authorizedEmail?: string;
  lastSyncAt?: string | null;
  selectedSources?: WorkspaceSource[];
  createGroupsFromOus?: boolean;
  createGroupsFromGroups?: boolean;
}

export type WorkspaceSyncModalMode = 'connect' | 'manage' | 'setup';

interface WorkspaceSyncModalProps {
  open: boolean;
  mode?: WorkspaceSyncModalMode;
  privilegeFailure?: boolean;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}

type WizardStep =
  | 'connect'
  | 'reconnect'
  | 'manage'
  | 'sources'
  | 'groups'
  | 'preview'
  | 'done'
  | 'privilege-failed';

const apiBase = () => (
  API_BASE_URL_WITH_API.endsWith('/api')
    ? API_BASE_URL_WITH_API
    : `${API_BASE_URL_WITH_API}/api`
);

const humanizeWorkspaceError = (status: number, data: { error?: string; code?: string } = {}) => {
  if (data.code === 'reconnect_required' || status === 401) {
    return {
      reconnect: true,
      privilegeFailure: false,
      message: 'Traffic Lite can no longer access the connected Google Workspace directory. Reconnect to continue syncing staff.'
    };
  }
  if (data.code === 'insufficient_google_privileges' || status === 403) {
    return {
      reconnect: false,
      privilegeFailure: true,
      message: 'Sync Failed. Google Admin Console access not found. Please contact your Google Administrator.'
    };
  }
  return {
    reconnect: false,
    privilegeFailure: false,
    message: data.error || 'Something went wrong. Please try again.'
  };
};

const primaryButtonStyle = (enabled: boolean): React.CSSProperties => ({
  padding: '8px 12px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: enabled ? '#4285f4' : '#9ca3af',
  color: '#fff',
  fontWeight: 700,
  cursor: enabled ? 'pointer' : 'not-allowed'
});

const secondaryButtonStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  background: '#fff',
  cursor: 'pointer'
};

const dangerButtonStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #fecaca',
  background: '#fff',
  color: '#991b1b',
  fontWeight: 600,
  cursor: 'pointer'
};

const formatLastSynced = (iso: string | null | undefined) => {
  if (!iso) return 'Never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const WorkspaceSyncModal = ({
  open,
  mode = 'connect',
  privilegeFailure = false,
  onClose,
  onChanged
}: WorkspaceSyncModalProps) => {
  const [step, setStep] = useState<WizardStep>('connect');
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Loading…');
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<WorkspaceConnection | null>(null);
  const [orgUnits, setOrgUnits] = useState<WorkspaceSource[]>([]);
  const [groups, setGroups] = useState<WorkspaceSource[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [createGroupsMode, setCreateGroupsMode] = useState<'unset' | 'no' | 'yes'>('unset');
  const [createGroupsFromOus, setCreateGroupsFromOus] = useState(false);
  const [createGroupsFromGroups, setCreateGroupsFromGroups] = useState(false);
  const [preview, setPreview] = useState<Record<string, number> | null>(null);
  const [syncResult, setSyncResult] = useState<Record<string, number> | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [previewBackStep, setPreviewBackStep] = useState<'manage' | 'groups'>('groups');

  const selectedSources = useMemo(() => {
    const all = [...orgUnits, ...groups];
    const fromDirectory = all.filter((source) => selectedKeys.has(`${source.type}:${source.id}`));
    if (fromDirectory.length > 0) return fromDirectory;
    return (connection?.selectedSources || []).filter((source) => (
      selectedKeys.has(`${source.type}:${source.id}`)
    ));
  }, [orgUnits, groups, selectedKeys, connection]);

  const groupChoiceValid = createGroupsMode === 'no'
    || (createGroupsMode === 'yes' && (createGroupsFromOus !== createGroupsFromGroups));

  const selectedOuCount = selectedSources.filter((source) => source.type === 'ou').length;
  const selectedGroupCount = selectedSources.filter((source) => source.type === 'group').length;

  const authHeaders = () => {
    const token = getEffectiveToken();
    if (!token) throw new Error('Not authenticated.');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const readError = async (response: Response) => {
    const data = await response.json().catch(() => ({}));
    return humanizeWorkspaceError(response.status, data);
  };

  const applyConnectionState = (current: WorkspaceConnection | null) => {
    setConnection(current);
    const fromOus = Boolean(current?.createGroupsFromOus);
    const fromGroups = Boolean(current?.createGroupsFromGroups);
    if (fromOus && fromGroups) {
      setCreateGroupsFromOus(true);
      setCreateGroupsFromGroups(false);
    } else {
      setCreateGroupsFromOus(fromOus);
      setCreateGroupsFromGroups(fromGroups);
    }
    if (fromOus || fromGroups) {
      setCreateGroupsMode('yes');
    } else if (current?.lastSyncAt) {
      setCreateGroupsMode('no');
    } else {
      setCreateGroupsMode('unset');
    }
    if (current?.selectedSources?.length) {
      setSelectedKeys(new Set(current.selectedSources.map((source) => `${source.type}:${source.id}`)));
    } else {
      setSelectedKeys(new Set());
    }
  };

  const loadStatus = async () => {
    const response = await fetch(`${apiBase()}/admin/workspace-sync/status`, {
      headers: { Authorization: authHeaders().Authorization }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Failed to load Workspace connection.');
    return data.connection as WorkspaceConnection | null;
  };

  const loadSources = async () => {
    const response = await fetch(`${apiBase()}/admin/workspace-sync/sources`, {
      headers: { Authorization: authHeaders().Authorization }
    });
    if (!response.ok) {
      const parsed = await readError(response);
      if (parsed.privilegeFailure) {
        setStep('privilege-failed');
        setError(null);
        return false;
      }
      if (parsed.reconnect) {
        setStep('reconnect');
        throw new Error(parsed.message);
      }
      throw new Error(parsed.message);
    }
    const data = await response.json().catch(() => ({}));
    setOrgUnits(Array.isArray(data.orgUnits) ? data.orgUnits : []);
    setGroups(Array.isArray(data.groups) ? data.groups : []);
    return true;
  };

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPreview(null);
    setSyncResult(null);
    setDisconnectOpen(false);
    setOrgUnits([]);
    setGroups([]);
    if (privilegeFailure) {
      setStep('privilege-failed');
      return;
    }
    void (async () => {
      try {
        setLoading(true);
        setLoadingLabel('Loading…');
        const current = await loadStatus();
        applyConnectionState(current);
        if (current?.status === 'connected') {
          if (mode === 'manage' && current.lastSyncAt) {
            setStep('manage');
          } else {
            // OAuth just completed, or Change Sync Settings / incomplete first sync.
            setStep('sources');
          }
        } else {
          setStep('connect');
          setCreateGroupsMode('unset');
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load Workspace Sync.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mode, privilegeFailure]);

  useEffect(() => {
    if (!open || (step !== 'sources' && step !== 'groups')) return;
    if (orgUnits.length > 0 || groups.length > 0) return;
    void (async () => {
      try {
        setLoading(true);
        setLoadingLabel('Loading directory…');
        await loadSources();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load sources.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, step]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !disconnectOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, disconnectOpen, onClose]);

  if (!open) return null;

  const handleConnect = async () => {
    try {
      setLoading(true);
      setLoadingLabel('Connecting…');
      setError(null);
      const response = await fetch(
        `${apiBase()}/admin/workspace-sync/connect-url?frontendOrigin=${encodeURIComponent(window.location.origin)}`,
        { headers: { Authorization: authHeaders().Authorization } }
      );
      const data = await response.json().catch(() => ({}));
      const parsed = humanizeWorkspaceError(response.status, data);
      if (parsed.privilegeFailure) {
        setStep('privilege-failed');
        setError(null);
        setLoading(false);
        return;
      }
      if (!response.ok) throw new Error(parsed.message);
      window.location.href = data.authUrl;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to connect Google Workspace.');
      setLoading(false);
    }
  };

  const draftSelectionPayload = () => ({
    sources: selectedSources.map((source) => ({ type: source.type, id: source.id, name: source.name })),
    createGroupsFromOus: createGroupsMode === 'yes' && createGroupsFromOus,
    createGroupsFromGroups: createGroupsMode === 'yes' && createGroupsFromGroups
  });

  const applyWorkspaceError = (parsed: ReturnType<typeof humanizeWorkspaceError>) => {
    if (parsed.privilegeFailure) {
      setStep('privilege-failed');
      setError(null);
    }
    if (parsed.reconnect) setStep('reconnect');
    throw new Error(parsed.message);
  };

  const runPreview = async () => {
    const response = await fetch(`${apiBase()}/admin/workspace-sync/preview`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(draftSelectionPayload())
    });
    if (!response.ok) {
      applyWorkspaceError(await readError(response));
    }
    const data = await response.json().catch(() => ({}));
    setPreview(data.preview || {});
    setStep('preview');
  };

  const runSync = async () => {
    const response = await fetch(`${apiBase()}/admin/workspace-sync/sync`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(draftSelectionPayload())
    });
    if (!response.ok) {
      applyWorkspaceError(await readError(response));
    }
    const data = await response.json().catch(() => ({}));
    setSyncResult(data.result || {});
    setStep('done');
    await onChanged();
    toast({ title: 'Google Workspace sync complete.' });
  };

  const confirmDisconnect = async () => {
    try {
      setLoading(true);
      setLoadingLabel('Disconnecting…');
      setError(null);
      const response = await fetch(`${apiBase()}/admin/workspace-sync/disconnect`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (!response.ok) {
        const parsed = await readError(response);
        throw new Error(parsed.message);
      }
      setDisconnectOpen(false);
      setConnection(null);
      setOrgUnits([]);
      setGroups([]);
      setSelectedKeys(new Set());
      await onChanged();
      toast({ title: 'Google Workspace disconnected.' });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect.');
    } finally {
      setLoading(false);
    }
  };

  const handleResyncNow = async () => {
    try {
      setLoading(true);
      setLoadingLabel('Preparing preview…');
      setError(null);
      setPreviewBackStep('manage');
      if (selectedKeys.size === 0 && connection?.selectedSources?.length) {
        setSelectedKeys(new Set(connection.selectedSources.map((source) => `${source.type}:${source.id}`)));
      }
      // Re-sync uses stored Directory credentials — no OAuth.
      await runPreview();
    } catch (err: unknown) {
      if (step !== 'privilege-failed') {
        setError(err instanceof Error ? err.message : 'Failed to prepare re-sync.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangeSettings = async () => {
    try {
      setLoading(true);
      setLoadingLabel('Loading directory…');
      setError(null);
      const loaded = await loadSources();
      if (!loaded) return;
      setStep('sources');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load sources.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSource = (source: WorkspaceSource) => {
    if (source.studentSourceBlocked) return;
    const key = `${source.type}:${source.id}`;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const memberCountLabel = (item: WorkspaceSource) => {
    if (typeof item.directMembersCount !== 'number' || !Number.isFinite(item.directMembersCount)) return null;
    return ` · ${item.directMembersCount} member${item.directMembersCount === 1 ? '' : 's'}`;
  };

  const renderSourceList = (title: string, items: WorkspaceSource[]) => (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700 }}>{title}</h4>
      <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        {items.length === 0 ? (
          <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '13px' }}>None found.</div>
        ) : items.map((item) => {
          const key = `${item.type}:${item.id}`;
          const checked = selectedKeys.has(key);
          return (
            <label
              key={key}
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
                padding: '8px 10px',
                borderBottom: '1px solid #f3f4f6',
                backgroundColor: item.studentSourceBlocked ? '#f9fafb' : '#fff',
                opacity: item.studentSourceBlocked ? 0.65 : 1
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={item.studentSourceBlocked}
                onChange={() => toggleSource(item)}
                aria-label={item.name}
              />
              <span>
                <span style={{ fontWeight: 600, fontSize: '13px' }}>{item.name}</span>
                {memberCountLabel(item) ? (
                  <span style={{ color: '#6b7280', fontSize: '12px' }}>{memberCountLabel(item)}</span>
                ) : null}
                {item.studentSourceWarning ? (
                  <div style={{ color: '#b45309', fontSize: '12px' }}>{item.studentSourceWarning}</div>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );

  const newCount = Number(syncResult?.newStaffCount ?? syncResult?.createdCount ?? preview?.newStaffCount ?? 0);
  const matchedCount = Number(syncResult?.matchedCount ?? syncResult?.updatedCount ?? preview?.matchedCount ?? 0);
  const groupsCount = Number(syncResult?.groupsCreatedCount ?? preview?.groupsToCreateCount ?? 0);
  const warningCount = Number(syncResult?.missingFromSourceCount ?? preview?.missingFromSourceCount ?? 0)
    + Number(syncResult?.suspendedCount ?? preview?.suspendedCount ?? 0);

  const modalTitle = (() => {
    if (disconnectOpen) return 'Manage Google Workspace Sync';
    if (step === 'done') return 'Sync Complete';
    if (step === 'privilege-failed') return 'Sync Failed';
    if (step === 'manage') return 'Manage Google Workspace Sync';
    if (step === 'reconnect') return 'Reconnect Google Workspace';
    if (step === 'connect') return 'Connect Google Workspace';
    return 'Sync with Google Workspace';
  })();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={modalTitle}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 80,
        padding: '20px'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '640px',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          padding: '20px',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>
          {modalTitle}
        </h3>
        {error && step !== 'privilege-failed' && (
          <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fef2f2', color: '#991b1b', fontSize: '13px' }}>
            {error}
          </div>
        )}
        {loading && (
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>{loadingLabel}</p>
        )}

        {disconnectOpen ? (
          <div>
            <p style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>Disconnect Google Workspace?</p>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
              Traffic Lite will stop syncing staff from Google Workspace. Existing Traffic Lite users and groups will remain unchanged.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                disabled={loading}
                onClick={() => setDisconnectOpen(false)}
                style={secondaryButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => void confirmDisconnect()}
                style={{ ...primaryButtonStyle(!loading), backgroundColor: loading ? '#9ca3af' : '#dc2626' }}
              >
                {loading ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        ) : (
          <>
        {step === 'privilege-failed' && (
          <div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
              Google Admin Console access not found. Please contact your Google Administrator.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={primaryButtonStyle(true)}>Close</button>
            </div>
          </div>
        )}

        {(step === 'connect' || step === 'reconnect') && (
          <div>
            {step === 'reconnect' ? (
              <p style={{ fontSize: '14px', color: '#374151', marginBottom: '10px' }}>
                Traffic Lite can no longer access the connected Google Workspace directory. Reconnect to continue syncing staff. This does not change Traffic Lite Admin access.
              </p>
            ) : (
              <>
                <p style={{ fontSize: '14px', color: '#374151', marginBottom: '10px' }}>
                  Automatically add and organize staff using your existing Google Workspace directory.
                </p>
                <p style={{ fontSize: '14px', color: '#374151', marginBottom: '10px' }}>
                  Traffic Lite receives read-only access needed for staff provisioning. Traffic Lite will not modify users, groups, or organizational units in Google Workspace.
                </p>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
                  The Google account that authorizes this connection must have Google Workspace Directory privileges. This is separate from Traffic Lite Admin access.
                </p>
              </>
            )}
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              <a
                href="https://www.trafficliteedu.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2563eb', textDecoration: 'underline' }}
              >
                Learn more about how Traffic Lite handles Google Workspace data
              </a>
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={onClose} style={secondaryButtonStyle}>Cancel</button>
              <button type="button" onClick={() => void handleConnect()} disabled={loading} style={primaryButtonStyle(!loading)}>
                {loading ? 'Connecting…' : (step === 'reconnect' ? 'Reconnect Google Workspace' : 'Connect Google Workspace')}
              </button>
            </div>
          </div>
        )}

        {step === 'manage' && (
          <div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
              Google Workspace is connected for this organization.
            </p>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Last synced
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginTop: '4px' }}>
                <span style={{ fontSize: '14px', color: '#111827' }}>
                  {formatLastSynced(connection?.lastSyncAt)}
                </span>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => void handleResyncNow()}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: loading ? '#9ca3af' : '#2563eb',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    padding: 0
                  }}
                >
                  {loading ? 'Preparing…' : 'Re-sync Now'}
                </button>
              </div>
            </div>
            {(selectedOuCount > 0 || selectedGroupCount > 0) && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Selected sources
                </div>
                <div style={{ fontSize: '14px', color: '#111827', marginTop: '4px' }}>
                  {[
                    selectedOuCount > 0 ? `${selectedOuCount} Organizational Unit${selectedOuCount === 1 ? '' : 's'}` : null,
                    selectedGroupCount > 0 ? `${selectedGroupCount} Google Group${selectedGroupCount === 1 ? '' : 's'}` : null
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <button
                type="button"
                disabled={loading}
                onClick={() => void handleChangeSettings()}
                style={secondaryButtonStyle}
              >
                Change Sync Settings
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => setDisconnectOpen(true)}
                style={dangerButtonStyle}
              >
                Disconnect Google Workspace
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={secondaryButtonStyle}>Close</button>
            </div>
          </div>
        )}

        {step === 'sources' && (
          <div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
              Traffic Lite only synchronizes staff from the sources you select. There is no import of the entire domain.
            </p>
            {renderSourceList('Organizational Units', orgUnits)}
            {renderSourceList('Google Groups', groups)}
            <p style={{ fontSize: '13px', color: '#374151' }}>{selectedSources.length} source{selectedSources.length === 1 ? '' : 's'} selected</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => {
                  if (connection?.lastSyncAt) setStep('manage');
                  else onClose();
                }}
                style={secondaryButtonStyle}
              >
                {connection?.lastSyncAt ? 'Back' : 'Cancel'}
              </button>
              <button
                type="button"
                disabled={selectedSources.length === 0 || loading}
                onClick={() => {
                  setError(null);
                  setStep('groups');
                }}
                style={primaryButtonStyle(selectedSources.length > 0 && !loading)}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 'groups' && (
          <div>
            <p style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>Create Traffic Lite Groups automatically?</p>
            <label style={{ display: 'flex', gap: '8px', marginBottom: '8px', fontSize: '14px' }}>
              <input
                type="radio"
                name="create-groups-mode"
                checked={createGroupsMode === 'no'}
                onChange={() => {
                  setCreateGroupsMode('no');
                  setCreateGroupsFromOus(false);
                  setCreateGroupsFromGroups(false);
                }}
              />
              No — add staff only. I will create my own groups within Traffic Lite.
            </label>
            <label style={{ display: 'flex', gap: '8px', marginBottom: '10px', fontSize: '14px' }}>
              <input
                type="radio"
                name="create-groups-mode"
                checked={createGroupsMode === 'yes'}
                onChange={() => {
                  setCreateGroupsMode('yes');
                  if (createGroupsFromOus && createGroupsFromGroups) {
                    setCreateGroupsFromGroups(false);
                  }
                }}
              />
              Yes — create groups from my Google directory
            </label>
            {createGroupsMode === 'yes' && (
              <div style={{ margin: '0 0 16px 24px' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 8px' }}>
                  Choose one source for Traffic Lite Groups.
                </p>
                <label style={{ display: 'flex', gap: '8px', marginBottom: '8px', fontSize: '14px' }}>
                  <input
                    type="radio"
                    name="create-groups-source"
                    checked={createGroupsFromOus}
                    onChange={() => {
                      setCreateGroupsFromOus(true);
                      setCreateGroupsFromGroups(false);
                    }}
                  />
                  Organizational Units
                </label>
                <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                  <input
                    type="radio"
                    name="create-groups-source"
                    checked={createGroupsFromGroups}
                    onChange={() => {
                      setCreateGroupsFromOus(false);
                      setCreateGroupsFromGroups(true);
                    }}
                  />
                  Google Groups
                </label>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={() => setStep('sources')} style={secondaryButtonStyle}>Back</button>
              <button
                type="button"
                disabled={loading || !groupChoiceValid}
                onClick={() => void (async () => {
                  try {
                    setLoading(true);
                    setLoadingLabel('Preparing preview…');
                    setError(null);
                    setPreviewBackStep('groups');
                    await runPreview();
                  } catch (err: unknown) {
                    if (step !== 'privilege-failed') {
                      setError(err instanceof Error ? err.message : 'Failed to preview sync.');
                    }
                  } finally {
                    setLoading(false);
                  }
                })()}
                style={primaryButtonStyle(!loading && groupChoiceValid)}
              >
                {loading ? 'Preparing…' : 'Review Sync'}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div>
            <h4 style={{ margin: '0 0 10px', fontSize: '15px' }}>Review Google Workspace Sync</h4>
            <ul style={{ margin: '0 0 12px', paddingLeft: '18px', fontSize: '14px', color: '#111827' }}>
              <li>{Number(preview?.newStaffCount || 0)} new staff users</li>
              <li>{Number(preview?.matchedCount || 0)} existing Traffic Lite users matched</li>
              <li>{Number(preview?.groupsToCreateCount || 0)} Traffic Lite Groups will be created</li>
              <li>{Number(preview?.missingFromSourceCount || 0)} synchronized users no longer found</li>
              {Number(preview?.suspendedCount || 0) > 0 ? (
                <li>{Number(preview.suspendedCount)} selected Google accounts are suspended</li>
              ) : null}
              {Number(preview?.skippedPossibleStudentCount || 0) > 0 ? (
                <li>{Number(preview.skippedPossibleStudentCount)} possible student accounts were excluded and not stored</li>
              ) : null}
            </ul>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              Nothing will be modified in Google Workspace. New synchronized users enter Traffic Lite as teachers. Existing Traffic Lite roles and Admin status are preserved.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setStep(previewBackStep)}
                style={secondaryButtonStyle}
              >
                Back
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => void (async () => {
                  try {
                    setLoading(true);
                    setLoadingLabel('Syncing…');
                    setError(null);
                    await runSync();
                  } catch (err: unknown) {
                    setError(err instanceof Error ? err.message : 'Sync failed. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                })()}
                style={primaryButtonStyle(!loading)}
              >
                {loading ? 'Syncing…' : 'Sync Staff'}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div>
            <p style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>✓ Google Workspace sync complete</p>
            <p style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
              {newCount + matchedCount} staff synchronized
            </p>
            <ul style={{ margin: '0 0 16px', paddingLeft: '18px', fontSize: '14px' }}>
              <li>{newCount} new Traffic Lite users added</li>
              <li>{matchedCount} existing users matched</li>
              <li>{groupsCount} Traffic Lite Group{groupsCount === 1 ? '' : 's'} created</li>
              <li>{warningCount} Workspace warning{warningCount === 1 ? '' : 's'}</li>
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={primaryButtonStyle(true)}>
                Done
              </button>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default WorkspaceSyncModal;
